import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-language";
import { supabase } from "@/integrations/supabase/client";
import { FamilyManager } from "@/components/FamilyManager";
import { FounderNote } from "@/components/FounderNote";
import { ParentInbox } from "@/components/ParentInbox";
import { SkillProgress, LearningJourneyTimeline } from "@/components/SkillProgress";
import { useAdmin } from "@/hooks/use-admin";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Pencil,
  Flame,
  Star,
  Clock,
  TrendingUp,
  Trophy,
  Sparkles,
  BookOpen,
  Video,
  Palette,
  Target,
  Heart,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/parent")({
  component: ParentRouteShell,
});

type Child = {
  id: string;
  name: string;
  age: number;
  age_tier: string;
  avatar_emoji: string;
  accent_color: string;
  total_points: number;
  current_streak: number;
  last_active_at: string | null;
};
type Event = {
  child_id: string;
  activity_type: "story" | "video" | "coloring" | "challenge" | string;
  points_earned: number;
  duration_seconds: number;
  created_at: string;
};
type Badge = {
  id: string;
  child_id: string;
  code: string;
  label_en: string;
  label_ar: string;
  emoji: string;
  earned_at: string;
};

const ACTIVITY_COLORS: Record<string, string> = {
  story: "hsl(var(--primary))",
  video: "hsl(var(--secondary))",
  coloring: "hsl(var(--accent))",
  challenge: "hsl(var(--clay))",
};

const ACTIVITY_ICONS: Record<string, React.ReactNode> = {
  story: <BookOpen className="size-4" />,
  video: <Video className="size-4" />,
  coloring: <Palette className="size-4" />,
  challenge: <Target className="size-4" />,
};

function ParentRouteShell() {
  const { pathname } = useLocation();
  return pathname === "/app/parent" ? <ParentDashboard /> : <Outlet />;
}

function ParentDashboard() {
  const { user } = useAuth();
  const { lang, dir } = useLang();
  const isAr = lang === "ar";
  const { isAdmin } = useAdmin();
  const [children, setChildren] = useState<Child[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [sub, setSub] = useState<{ tier: string; status: string } | null>(null);
  const [selectedId, setSelectedId] = useState<string | "all">("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      const [{ data: kids }, { data: evs }, { data: bs }, { data: s }] = await Promise.all([
        supabase
          .from("children")
          .select("id, name, age, age_tier, avatar_emoji, accent_color, total_points, current_streak, last_active_at")
          .order("created_at"),
        supabase
          .from("progress_events")
          .select("child_id, activity_type, points_earned, duration_seconds, created_at")
          .gte("created_at", new Date(Date.now() - 30 * 86400000).toISOString())
          .order("created_at", { ascending: false }),
        supabase
          .from("badges")
          .select("id, child_id, code, label_en, label_ar, emoji, earned_at")
          .order("earned_at", { ascending: false }),
        supabase.from("subscriptions").select("tier, status").eq("user_id", user.id).single(),
      ]);
      setChildren((kids as Child[]) ?? []);
      setEvents((evs as Event[]) ?? []);
      setBadges((bs as Badge[]) ?? []);
      if (s) setSub(s);
      setLoading(false);
    })();
  }, [user]);

  const filteredEvents = useMemo(
    () => (selectedId === "all" ? events : events.filter((e) => e.child_id === selectedId)),
    [events, selectedId],
  );
  const filteredBadges = useMemo(
    () => (selectedId === "all" ? badges : badges.filter((b) => b.child_id === selectedId)),
    [badges, selectedId],
  );
  const selectedChild = selectedId === "all" ? null : children.find((c) => c.id === selectedId);

  // 7-day chart data
  const weekData = useMemo(() => {
    const days: { day: string; label: string; count: number; minutes: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      const next = new Date(d);
      next.setDate(d.getDate() + 1);
      const inDay = filteredEvents.filter(
        (e) => new Date(e.created_at) >= d && new Date(e.created_at) < next,
      );
      days.push({
        day: d.toISOString().slice(0, 10),
        label: d.toLocaleDateString(isAr ? "ar" : "en", { weekday: "short" }),
        count: inDay.length,
        minutes: Math.round(inDay.reduce((s, e) => s + e.duration_seconds, 0) / 60),
      });
    }
    return days;
  }, [filteredEvents, isAr]);

  const weekEvents = weekData.reduce((s, d) => s + d.count, 0);
  const weekMinutes = weekData.reduce((s, d) => s + d.minutes, 0);
  const weekPoints = filteredEvents
    .filter((e) => Date.now() - new Date(e.created_at).getTime() < 7 * 86400000)
    .reduce((s, e) => s + e.points_earned, 0);

  // Activity breakdown
  const byType = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredEvents.forEach((e) => (counts[e.activity_type] = (counts[e.activity_type] ?? 0) + 1));
    return Object.entries(counts).map(([type, value]) => ({ type, value }));
  }, [filteredEvents]);

  // Insights
  const insights = useMemo(() => buildInsights(children, events, selectedChild, isAr), [
    children,
    events,
    selectedChild,
    isAr,
  ]);

  // Family aggregates
  const familyPoints = children.reduce((s, c) => s + c.total_points, 0);
  const familyStreak = children.reduce((m, c) => Math.max(m, c.current_streak), 0);

  if (loading) {
    return (
      <div dir={dir} className="px-5 py-7 max-w-2xl mx-auto pb-12 animate-fade-in">
        <div className="mb-6 space-y-2">
          <div className="h-3 w-24 rounded-full bg-clay/40 animate-pulse" />
          <div className="h-9 w-3/4 rounded-xl bg-clay/40 animate-pulse" />
          <div className="h-4 w-1/2 rounded-full bg-clay/30 animate-pulse" />
        </div>
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 rounded-2xl bg-card border-2 border-clay/30 animate-pulse" />
          ))}
        </div>
        <div className="h-16 rounded-2xl bg-card border-2 border-clay/30 animate-pulse mb-6" />
        <div className="h-48 rounded-3xl bg-card border-2 border-clay/30 animate-pulse mb-6" />
        <div className="h-64 rounded-3xl bg-card border-2 border-clay/30 animate-pulse" />
      </div>
    );
  }

  const hour = new Date().getHours();
  const greetingEn = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const greetingAr = hour < 12 ? "صباح الخير" : hour < 18 ? "مساء النور" : "مساء الخير";

  return (
    <div dir={dir} className="px-5 py-7 max-w-2xl mx-auto pb-12">
      {/* Greeting */}
      <div className="mb-6 animate-fade-in">
        <p className="font-display-en text-xs uppercase tracking-[0.3em] text-primary font-bold mb-1">
          {isAr ? greetingAr : greetingEn} ☀️
        </p>
        <h1
          className={`${isAr ? "font-display-ar" : "font-display-en"} text-3xl md:text-4xl font-bold tracking-tight`}
        >
          {isAr ? "تابع رحلة نمو طفلك" : "Watch them grow."}
        </h1>
        <p className="text-sm text-foreground/60 mt-1">
          {isAr
            ? "كل لحظة نمو تُحتفل بها."
            : "Every moment of growth, celebrated."}
        </p>
      </div>

      {/* This week with your family — soft, warm, family-centered entry */}
      <Link
        to="/app/parent/this-week"
        className="block bg-gradient-to-br from-primary/12 via-accent/8 to-secondary/8 border-2 border-primary/25 rounded-3xl p-5 mb-3 flex items-center gap-3 hover:-translate-y-0.5 transition-all"
      >
        <div className="size-12 rounded-2xl bg-card flex items-center justify-center text-2xl shadow-sm">🤍</div>
        <div className="flex-1">
          <p className={`${isAr ? "ar-display" : "font-display-en"} text-base font-bold leading-tight`}>
            {isAr ? "هذا الأسبوع مع عائلتك" : "This week with your family"}
          </p>
          <p className={`${isAr ? "ar-story" : ""} text-[11px] text-foreground/60 mt-0.5 leading-relaxed`}>
            {isAr
              ? "همسة لطيفة، لحظة عائلية، وتأمّل صغير"
              : "A gentle prompt, a family moment, a small reflection"}
          </p>
        </div>
        <span className="text-primary font-bold rtl:rotate-180">→</span>
      </Link>

      {/* Smaller "Today" link kept as a secondary entry */}
      <Link
        to="/app/parent/today"
        className="block text-center text-[11px] font-bold text-foreground/55 hover:text-foreground/80 mb-5 transition-colors"
      >
        {isAr ? "أو لقطة اليوم →" : "or today's snapshot →"}
      </Link>

      {/* A quiet note from the founder — rare, warm, never marketing. */}
      <FounderNote />

      {/* Messages from the Jozori team (visible even when empty). */}
      <ParentInbox isAr={isAr} />

      {/* Family overview */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <OverviewStat
          icon={<Users className="size-4" />}
          label={isAr ? "الأطفال" : "Children"}
          value={children.length}
          color="bg-primary/15 text-primary"
        />
        <OverviewStat
          icon={<Star className="size-4 fill-current" />}
          label={isAr ? "النقاط" : "Points"}
          value={familyPoints}
          color="bg-secondary/15 text-secondary"
        />
        <OverviewStat
          icon={<Flame className="size-4" />}
          label={isAr ? "أطول سلسلة" : "Top streak"}
          value={`${familyStreak}d`}
          color="bg-accent/15 text-accent"
        />
      </div>

      {/* Plan */}
      {sub && (
        <Link
          to="/app/pricing"
          className="block bg-card border-2 border-clay/30 rounded-2xl p-4 mb-6 flex items-center justify-between hover:border-primary transition-colors"
        >
          <div>
            <p className="text-[10px] uppercase tracking-widest text-foreground/50 font-bold">
              {isAr ? "الخطة" : "Plan"}
            </p>
            <p className="font-display-en text-lg font-bold capitalize">{sub.tier}</p>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-bold ${
              sub.status === "active"
                ? "bg-secondary/15 text-secondary"
                : "bg-clay/40 text-foreground/60"
            }`}
          >
            {sub.status}
          </span>
        </Link>
      )}

      {/* Child switcher */}
      {children.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-widest text-foreground/60 mb-2">
            {isAr ? "اعرض حسب" : "View"}
          </p>
          <div className="flex gap-2 overflow-x-auto -mx-5 px-5 pb-2 snap-x">
            <SwitchPill
              active={selectedId === "all"}
              onClick={() => setSelectedId("all")}
              emoji="👨‍👩‍👧"
              label={isAr ? "الكل" : "All"}
            />
            {children.map((c) => (
              <SwitchPill
                key={c.id}
                active={selectedId === c.id}
                onClick={() => setSelectedId(c.id)}
                emoji={c.avatar_emoji}
                label={c.name}
              />
            ))}
          </div>
        </div>
      )}

      {children.length === 0 ? (
        <div className="bg-card border-2 border-dashed border-clay/40 rounded-3xl p-10 text-center">
          <Sparkles className="size-8 mx-auto mb-3 text-primary" />
          <p className="text-foreground/60 mb-4">
            {isAr ? "لم تتم إضافة أطفال بعد" : "No children added yet."}
          </p>
          <Link
            to="/onboarding"
            className="inline-block px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-bold text-sm"
          >
            {isAr ? "إضافة طفل" : "Add a child"}
          </Link>
        </div>
      ) : (
        <>
          {/* Selected child hero */}
          {selectedChild && (
            <div className="bg-gradient-to-br from-primary/15 via-accent/10 to-secondary/10 rounded-3xl p-5 mb-5 flex items-center gap-4 animate-scale-in">
              <div className="text-5xl">{selectedChild.avatar_emoji}</div>
              <div className="flex-1">
                <h2 className="font-display-en text-xl font-bold">{selectedChild.name}</h2>
                <p className="text-xs text-foreground/60">
                  {isAr ? `العمر ${selectedChild.age}` : `Age ${selectedChild.age}`} ·{" "}
                  {selectedChild.last_active_at
                    ? new Date(selectedChild.last_active_at).toLocaleDateString(isAr ? "ar" : "en")
                    : isAr
                      ? "لم يبدأ بعد"
                      : "Not started"}
                </p>
                <div className="flex gap-3 mt-1.5 text-sm">
                  <span className="font-bold text-primary">⭐ {selectedChild.total_points}</span>
                  <span className="font-bold text-secondary">🔥 {selectedChild.current_streak}d</span>
                </div>
              </div>
              <Link
                to="/app/child/$childId/edit"
                params={{ childId: selectedChild.id }}
                className="size-9 rounded-full bg-card/80 hover:bg-card flex items-center justify-center transition-colors"
                aria-label="Edit"
              >
                <Pencil className="size-4" />
              </Link>
            </div>
          )}

          {/* This week summary */}
          <div className="bg-card border-2 border-clay/30 rounded-3xl p-5 mb-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display-en text-base font-bold flex items-center gap-2">
                <TrendingUp className="size-4 text-primary" />
                {isAr ? "هذا الأسبوع" : "This week"}
              </h3>
              <span className="text-xs text-foreground/50">{isAr ? "آخر ٧ أيام" : "Last 7 days"}</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <Stat label={isAr ? "نشاط" : "Activities"} value={weekEvents} color="text-primary" />
              <Stat label={isAr ? "نقاط" : "Points"} value={weekPoints} color="text-secondary" />
              <Stat
                label={isAr ? "دقائق" : "Minutes"}
                value={weekMinutes}
                color="text-accent"
                icon={<Clock className="size-3" />}
              />
            </div>
            <div className="h-32" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "hsl(var(--foreground) / 0.5)" }}
                  />
                  <YAxis hide />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: "2px solid hsl(var(--border))",
                      backgroundColor: "hsl(var(--card))",
                      fontSize: 12,
                    }}
                    cursor={{ fill: "hsl(var(--primary) / 0.08)" }}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Activity breakdown */}
          {byType.length > 0 && (
            <div className="bg-card border-2 border-clay/30 rounded-3xl p-5 mb-5">
              <h3 className="font-display-en text-base font-bold mb-4">
                {isAr ? "تنوع الأنشطة" : "Activity mix"}
              </h3>
              <div className="flex items-center gap-4">
                <div className="size-28 shrink-0" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={byType}
                        dataKey="value"
                        innerRadius={28}
                        outerRadius={50}
                        paddingAngle={3}
                      >
                        {byType.map((entry) => (
                          <Cell
                            key={entry.type}
                            fill={ACTIVITY_COLORS[entry.type] ?? "hsl(var(--muted))"}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {byType.map((b) => {
                    const total = byType.reduce((s, x) => s + x.value, 0);
                    const pct = Math.round((b.value / total) * 100);
                    return (
                      <div key={b.type}>
                        <div className="flex items-center gap-2 mb-1">
                          <span style={{ color: ACTIVITY_COLORS[b.type] }}>
                            {ACTIVITY_ICONS[b.type] ?? <Sparkles className="size-4" />}
                          </span>
                          <span className="text-xs font-bold capitalize flex-1">{b.type}</span>
                          <span className="text-xs font-bold text-foreground/60">{pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-clay/30 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: ACTIVITY_COLORS[b.type],
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Skill progression */}
          <div className="mb-5">
            <SkillProgress
              events={filteredEvents}
              isAr={isAr}
              childName={selectedChild?.name}
            />
          </div>

          {/* Learning journey timeline */}
          <div className="mb-5">
            <LearningJourneyTimeline events={filteredEvents} isAr={isAr} />
          </div>


          {/* Insights */}
          {insights.length > 0 && (
            <div className="bg-gradient-to-br from-accent/10 to-primary/5 border-2 border-accent/30 rounded-3xl p-5 mb-5 animate-fade-in">
              <h3 className="font-display-en text-base font-bold mb-3 flex items-center gap-2">
                <Heart className="size-4 text-accent fill-accent/40" />
                {isAr ? "ملاحظات لك" : "For you"}
              </h3>
              <div className="space-y-3">
                {insights.map((ins, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className="text-xl shrink-0">{ins.emoji}</div>
                    <p className="text-sm text-foreground/80 leading-relaxed flex-1">{ins.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Achievement timeline */}
          {filteredBadges.length > 0 && (
            <div className="bg-card border-2 border-clay/30 rounded-3xl p-5 mb-5">
              <h3 className="font-display-en text-base font-bold mb-4 flex items-center gap-2">
                <Trophy className="size-4 text-primary" />
                {isAr ? "خط الإنجازات" : "Achievement timeline"}
              </h3>
              <div className="space-y-3">
                {filteredBadges.slice(0, 8).map((b, idx) => (
                  <div
                    key={b.id}
                    className="flex items-center gap-3 animate-fade-in"
                    style={{ animationDelay: `${idx * 50}ms`, animationFillMode: "backwards" }}
                  >
                    <div className="size-10 rounded-2xl bg-primary/15 flex items-center justify-center text-xl shrink-0">
                      {b.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">
                        {isAr ? b.label_ar : b.label_en}
                      </p>
                      <p className="text-[11px] text-foreground/50">
                        {new Date(b.earned_at).toLocaleDateString(isAr ? "ar" : "en", {
                          month: "short",
                          day: "numeric",
                        })}
                        {selectedId === "all" && (
                          <>
                            {" · "}
                            {children.find((c) => c.id === b.child_id)?.name}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All-children quick grid (only when 'all') */}
          {selectedId === "all" && children.length > 1 && (
            <div className="mb-5">
              <h3 className="font-display-en text-base font-bold mb-3">
                {isAr ? "نظرة سريعة" : "At a glance"}
              </h3>
              <div className="space-y-3">
                {children.map((c) => {
                  const evCount = events.filter(
                    (e) =>
                      e.child_id === c.id &&
                      Date.now() - new Date(e.created_at).getTime() < 7 * 86400000,
                  ).length;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setSelectedId(c.id)}
                      className="w-full bg-card border-2 border-clay/30 rounded-2xl p-4 flex items-center gap-3 hover:border-primary text-left transition-colors"
                    >
                      <div className="text-3xl">{c.avatar_emoji}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold truncate">{c.name}</p>
                        <p className="text-xs text-foreground/60">
                          ⭐ {c.total_points} · 🔥 {c.current_streak}d · {evCount}{" "}
                          {isAr ? "نشاط" : "activities"}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Admin shortcut */}
      {isAdmin && (
        <Link
          to="/app/admin"
          className="mt-6 block bg-gradient-to-br from-primary/15 to-accent/10 border-2 border-primary/30 rounded-2xl p-4 flex items-center gap-3 hover:scale-[1.01] transition-transform"
        >
          <div className="size-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <Sparkles className="size-5" />
          </div>
          <div className="flex-1">
            <p className="font-display-en text-sm font-bold">
              {isAr ? "استوديو المحتوى" : "Content Studio"}
            </p>
            <p className="text-[11px] text-foreground/60">
              {isAr ? "إدارة قصص، فيديوهات وأنشطة" : "Manage stories, videos & activities"}
            </p>
          </div>
        </Link>
      )}

      {/* Family */}
      <div className="mt-8 pt-6 border-t border-clay/30">
        <FamilyManager />
      </div>
    </div>
  );
}

function SwitchPill({
  active,
  onClick,
  emoji,
  label,
}: {
  active: boolean;
  onClick: () => void;
  emoji: string;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`snap-start shrink-0 flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all ${
        active
          ? "bg-primary text-primary-foreground border-primary scale-105"
          : "bg-card border-clay/40 hover:border-primary"
      }`}
    >
      <span className="text-lg">{emoji}</span>
      <span className="text-sm font-bold">{label}</span>
    </button>
  );
}

function OverviewStat({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
}) {
  return (
    <div className="bg-card border-2 border-clay/30 rounded-2xl p-3 text-center">
      <div className={`size-8 mx-auto rounded-xl flex items-center justify-center mb-1.5 ${color}`}>
        {icon}
      </div>
      <p className="font-display-en text-xl font-black">{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-foreground/60 font-bold">{label}</p>
    </div>
  );
}

function Stat({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: number | string;
  color: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="bg-clay/15 rounded-2xl py-3 text-center">
      <p className={`font-display-en text-2xl font-black ${color} flex items-center justify-center gap-1`}>
        {icon}
        {value}
      </p>
      <p className="text-[10px] uppercase tracking-widest font-bold text-foreground/60">{label}</p>
    </div>
  );
}

type Insight = { emoji: string; text: string };

function buildInsights(
  children: Child[],
  events: Event[],
  selected: Child | null | undefined,
  isAr: boolean,
): Insight[] {
  const ins: Insight[] = [];
  const week = events.filter(
    (e) => Date.now() - new Date(e.created_at).getTime() < 7 * 86400000,
  );

  if (selected) {
    const mine = week.filter((e) => e.child_id === selected.id);
    if (mine.length === 0) {
      ins.push({
        emoji: "🌱",
        text: isAr
          ? `لم يلعب ${selected.name} هذا الأسبوع. جلسة ٥ دقائق قد تعيد الزخم.`
          : `${selected.name} hasn't played this week. A 5-minute session can spark momentum.`,
      });
    } else if (selected.current_streak >= 7) {
      ins.push({
        emoji: "🔥",
        text: isAr
          ? `${selected.current_streak} يومًا متتاليًا! احتفل بـ${selected.name} الليلة.`
          : `${selected.current_streak}-day streak! Celebrate ${selected.name} tonight.`,
      });
    } else if (mine.length >= 10) {
      ins.push({
        emoji: "✨",
        text: isAr
          ? `${selected.name} نشيط جدًا هذا الأسبوع — ${mine.length} نشاط مكتمل.`
          : `${selected.name} is on fire — ${mine.length} activities this week.`,
      });
    }
    const types = new Set(mine.map((e) => e.activity_type));
    if (mine.length > 0 && types.size === 1) {
      const only = [...types][0];
      ins.push({
        emoji: "🎨",
        text: isAr
          ? `يركز ${selected.name} على ${only} فقط. جرب نوعًا جديدًا اليوم.`
          : `${selected.name} only does ${only}. Try a new activity type today.`,
      });
    }
  } else {
    if (children.length === 0) return ins;
    const topKid = [...children].sort((a, b) => b.current_streak - a.current_streak)[0];
    if (topKid.current_streak >= 3) {
      ins.push({
        emoji: "🏆",
        text: isAr
          ? `${topKid.name} يقود العائلة بسلسلة ${topKid.current_streak} أيام.`
          : `${topKid.name} leads the family with a ${topKid.current_streak}-day streak.`,
      });
    }
    const quiet = children.filter(
      (c) => !week.some((e) => e.child_id === c.id),
    );
    if (quiet.length > 0) {
      ins.push({
        emoji: "💌",
        text: isAr
          ? `${quiet.map((c) => c.name).join(" و")} لم يلعبوا هذا الأسبوع.`
          : `${quiet.map((c) => c.name).join(" and ")} haven't played this week.`,
      });
    }
    if (week.length >= 20) {
      ins.push({
        emoji: "🌟",
        text: isAr
          ? `أسبوع رائع: ${week.length} نشاط عبر العائلة!`
          : `Amazing week: ${week.length} family activities completed!`,
      });
    }
  }

  if (ins.length === 0) {
    ins.push({
      emoji: "🌿",
      text: isAr
        ? "كل بداية صغيرة هي بذرة. أنت تفعل عملًا جميلًا."
        : "Every small start is a seed. You're doing beautiful work.",
    });
  }
  return ins;
}
